import * as React from "react";
import StartupSplash from "../Authentication/StartupSplash";
import { LogClientDiagnostic } from "source/app/services/ClientDiagnostics";
import {
    CheckForClientUpdateAsync,
    clientUpdateCheckIntervalMs,
    SubscribeToClientUpdate,
} from "source/app/services/ClientUpdate/ClientUpdateChecker";
import ClientUpdateNotice from "./ClientUpdateNotice";

type ClientVersionState = "checking" | "current" | "updated";

export default function ClientVersionGate({
    children,
}: {
    children: React.ReactNode;
}) {
    const [state, setState] = React.useState<ClientVersionState>("checking");

    React.useEffect(() => {
        let mounted = true;
        const handleUpdate = () => {
            if (!mounted) return;

            // An update found before initial validation completes is unsafe to
            // mount. Once the active session has passed that validation, leave
            // it usable while ClientUpdateNotice counts down to its refresh.
            setState((currentState) =>
                currentState === "checking" ? "updated" : currentState
            );
        };
        const unsubscribe = SubscribeToClientUpdate(handleUpdate);

        const applyCheckResult = (
            result: "current" | "updated" | "unavailable",
            isInitialCheck: boolean
        ) => {
            if (!mounted) return;

            if (result === "updated") {
                handleUpdate();
                return;
            }

            if (result === "unavailable" && isInitialCheck) {
                LogClientDiagnostic(
                    "client-update",
                    "version-check-unavailable",
                    {},
                    "error"
                );
            }

            setState((currentState) =>
                currentState === "updated" ? currentState : "current"
            );
        };

        const checkForUpdate = (isInitialCheck = false) => {
            if (!isInitialCheck && document.visibilityState !== "visible") {
                return;
            }

            void CheckForClientUpdateAsync().then((result) => {
                applyCheckResult(result, isInitialCheck);
            });
        };

        checkForUpdate(true);
        const handleVisibilityChange = () => checkForUpdate();
        document.addEventListener("visibilitychange", handleVisibilityChange);
        const intervalId = window.setInterval(
            handleVisibilityChange,
            clientUpdateCheckIntervalMs
        );

        return () => {
            mounted = false;
            unsubscribe();
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
            window.clearInterval(intervalId);
        };
    }, []);

    return (
        <>
            <ClientUpdateNotice />
            {state === "checking" && <StartupSplash completed={1} />}
            {state === "updated" && <VersionUpdatedStatus />}
            {state === "current" && children}
        </>
    );
}

function VersionUpdatedStatus() {
    return (
        <div id="pnlVersionUpdated" role="status" aria-live="polite">
            <p style={{ textAlign: "center" }}>
                A new version is available. Please wait for the refresh.
            </p>
        </div>
    );
}
